/**
 * A filter the alert sheet adds to a saved view: the value a field and an
 * operator take, in the table grammar the page's own URL speaks.
 *
 * Pure, and React-free. The endpoint checks the view against the report's own
 * validator, so this only keeps the sheet from offering an empty filter.
 */

import type { AlertFilterField } from './alert-rules-api.ts'
import { LIST_SEPARATOR, RANGE_SEPARATOR } from './column-filter.ts'

/** How the sheet asks for a value. */
export type FilterInput =
  | { kind: 'choice'; options: readonly { value: string; label: string }[] }
  | { kind: 'range'; type: 'number' | 'text' }
  | { kind: 'text'; type: 'number' | 'text'; hint?: string; list?: boolean }

const YES_NO = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' }
] as const

const EMPTY = [
  { value: 'true', label: 'Is empty' },
  { value: 'false', label: 'Is not empty' }
] as const

/**
 * How the sheet asks for the value of one field and operator.
 *
 * @param field - The field.
 * @param op - The operator.
 * @returns The input to show.
 */
export function filterInput(field: AlertFilterField, op: string): FilterInput {
  const numeric = field.type === 'number' ? 'number' : 'text'
  if (op === 'isnull') {
    return { kind: 'choice', options: EMPTY }
  }
  if (field.type === 'boolean') {
    return { kind: 'choice', options: YES_NO }
  }
  if (field.type === 'enum' && op === 'exact') {
    return {
      kind: 'choice',
      options: (field.values ?? []).map(value => ({ value, label: value }))
    }
  }
  if (op === 'range') {
    return { kind: 'range', type: numeric }
  }
  if (op === 'in') {
    const values = field.values?.join(', ')

    return {
      kind: 'text',
      type: 'text',
      list: true,
      hint: values
        ? `Separate values with commas: ${values}.`
        : 'Separate values with commas.'
    }
  }
  if (field.type === 'date' || field.type === 'datetime') {
    return { kind: 'text', type: 'text', hint: 'A date, such as 2026-10-01.' }
  }

  return { kind: 'text', type: numeric }
}

/**
 * The value a filter carries in the URL, or null while it is incomplete.
 *
 * @param input - How the sheet asked for it.
 * @param first - The value, or a range's low end.
 * @param second - A range's high end.
 * @returns The value.
 */
export function filterValue(
  input: FilterInput,
  first: string,
  second = ''
): string | null {
  const low = first.trim()
  const high = second.trim()
  if (input.kind === 'range') {
    return low === '' || high === '' ? null : `${low}${RANGE_SEPARATOR}${high}`
  }
  if (input.kind === 'text' && input.list) {
    const parts = low
      .split(LIST_SEPARATOR)
      .map(part => part.trim())
      .filter(part => part !== '')

    return parts.length === 0 ? null : parts.join(LIST_SEPARATOR)
  }

  return low === '' ? null : low
}
